import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { AuthService } from 'src/app/modules/auth';
import { ApiService } from '../http/api.service';
import { AuthRecoveryService } from '../http/auth-recovery.service';
import { authLocalStorageToken, LOGIN_PATH_SELECT_ACCOUNT, odooInfo, takeReturnUrl } from '../shared/common/helper';
import swal from 'sweetalert';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class SSOMsalService {

  constructor(
    private authMsalService: MsalService,
    private authService: AuthService,
    private api: ApiService,
    private translate: TranslateService,
    private authRecovery: AuthRecoveryService
  ) {
  }

  /**
   * storage sso msal token
   * @param payload payload data from msal when login success
   */
  storageSsoMsalToken(payload, router) {
    this.authService.loginOdooSystem({ accessToken: payload.accessToken })
      .subscribe({
        next: (odooRes) => this.handleOdooLoginResponse(odooRes, payload, router),
        error: (error) => {
          // errorMessage() đã tự tắt loading và hiện popup
          this.api.errorMessage(error);
        }
      })
  }

  /**
   * Xử lý response đăng nhập Odoo.
   * Tách riêng khỏi storageSsoMsalToken để bọc try/catch: nếu code trong này ném lỗi
   * thì RxJS KHÔNG đưa vào nhánh error của subscribe (lỗi từ next handler bị ném ra
   * global), người dùng sẽ đứng im ở /callbackmsal với spinner quay mãi.
   * @param odooRes response trả về từ API sign_in
   * @param payload AuthenticationResult của MSAL
   * @param router router để điều hướng sau khi lưu token
   */
  private handleOdooLoginResponse(odooRes: any, payload: any, router: any): void {
    try {
      /**
       * Đọc từng field bằng optional chaining thay vì destructuring lồng nhau.
       * Khi đăng nhập thất bại backend thường trả data: null - destructuring
       * `data: { access_token }` sẽ ném TypeError NGAY trước cả câu if kiểm tra code,
       * khiến nhánh báo lỗi bên dưới không bao giờ chạy được.
       */
      const result = odooRes?.result;
      const code = result?.code;
      const message = result?.message;
      const tokenOdoo = result?.data?.access_token;
      const employeeId = result?.data?.employee_id?.id;

      // Đăng nhập Odoo thất bại
      if (code !== 200) {
        const userNotFoundMsg = "Không thể đăng nhập với user này. Vui lòng đăng nhập user khác!";
        this.showLoginErrorAndLogout(message ?? userNotFoundMsg);
        return;
      }

      // Tài khoản AAD chưa được gắn với nhân viên trong Odoo
      if (!employeeId) {
        const userNotLinkMsg = "User này chưa được gắn người dùng. Vui lòng đăng nhập user khác!";
        this.showLoginErrorAndLogout(userNotLinkMsg);
        return;
      }

      // Hợp lệ: lưu token rồi lấy thông tin người dùng
      localStorage.setItem(authLocalStorageToken, JSON.stringify({
        accessToken: payload.accessToken,
        // expires: payload.expiresOn.getTime(),
        idToken: payload.idToken,
        employeeID: employeeId,
        tokenOdoo: tokenOdoo
      }));
      localStorage.setItem(odooInfo, JSON.stringify(result?.data));
      this.getUserTokenAndRedirectHomePage((<AuthenticationResult>payload), router);
    } catch (error) {
      // Response sai định dạng so với mong đợi - báo cho người dùng thay vì treo màn hình
      console.error('[SSO] Không xử lý được response đăng nhập Odoo.', { odooRes, error });
      this.showLoginErrorAndLogout('Không xử lý được phản hồi đăng nhập. Vui lòng thử lại!');
    }
  }

  /**
   * Hiện popup lỗi đăng nhập rồi đưa về trang đăng nhập để dùng tài khoản khác.
   * Dùng logoutLocal() chứ KHÔNG dùng logout(): tài khoản AAD vẫn hợp lệ, lỗi nằm ở phía
   * portal (chưa gắn nhân viên, backend trả lỗi). Đăng xuất toàn cục ở đây sẽ huỷ session
   * SSO của Azure AD và đăng xuất luôn mọi ứng dụng khác dùng chung tenant.
   * Kèm selectAccount vì session SSO còn nguyên - không có cờ này thì bấm đăng nhập lại
   * sẽ tự vào đúng tài khoản vừa lỗi và người dùng kẹt trong vòng lặp.
   * @param title nội dung lỗi hiển thị cho người dùng
   */
  private showLoginErrorAndLogout(title: string): void {
    this.api.loadingOff();
    this.api.loadingCustomOff();

    swal({
      icon: "error",
      title,
      buttons: {
        ok: this.translate.instant('COMMON.btn.ok'),
      },
    } as any).then(() => {
      this.authService.logoutLocal(LOGIN_PATH_SELECT_ACCOUNT);
    })
  }

  /**
   * check active account
   */
  checkAndSetActiveAccount(){
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authMsalService.instance.getActiveAccount();

    if (!activeAccount && this.authMsalService.instance.getAllAccounts().length > 0) {
      let accounts = this.authMsalService.instance.getAllAccounts();
      this.authMsalService.instance.setActiveAccount(accounts[0]);
    }
  }

  /**
   * Phục hồi phiên đăng nhập khi luồng SSO callback thất bại.
   * Uỷ quyền cho AuthRecoveryService - service dùng chung với InterceptorService, nhờ đó
   * luồng callback cũng được hưởng guard lệch giờ, bộ đếm chống vòng lặp và xử lý khi
   * chính loginRedirect thất bại.
   * Trước đây hàm này là bản sao của logic bên interceptor với cờ chống trùng RIÊNG,
   * nên hai luồng có thể cùng hiện popup và cùng gọi loginRedirect.
   * @param error lỗi dẫn tới việc phải phục hồi
   */
  recoverAuthentication(error: any): void {
    // recover() trả về observable cold nên bắt buộc phải subscribe, nếu không popup không hiện
    this.authRecovery.recover(error).subscribe();
  }
  /**
   * Lấy thông tin nhân viên rồi điều hướng về trang chủ.
   * @param payload AuthenticationResult của MSAL
   * @param router router để điều hướng
   */
  getUserTokenAndRedirectHomePage(payload, router){
    this.authService.getUserByToken()
    .subscribe({
      next: () => {
        this.authMsalService.instance.setActiveAccount(payload.account);
        /**
         * Về đúng trang người dùng định vào (link từ mail), không mặc định về trang chủ.
         * navigateByUrl vì đích có thể kèm query string.
         */
        router.navigateByUrl(takeReturnUrl());
      },
      /**
       * Không dùng recoverAuthentication() ở đây: token MSAL vẫn hợp lệ, lỗi nằm ở
       * API nghiệp vụ. Nếu loginRedirect lại sẽ tạo vòng lặp đăng nhập vô tận khi
       * backend đang chết. Đăng xuất hẳn để người dùng chủ động thử lại.
       */
      error: (error) => {
        console.error('[SSO] Không lấy được thông tin người dùng sau khi đăng nhập.', error);
        this.showLoginErrorAndLogout('Không lấy được thông tin người dùng. Vui lòng đăng nhập lại!');
      }
    })
  }
}